import {
  trackEvent,
  type AnalyticsEventName,
  type AnalyticsParams,
} from './analytics';

export interface LifecycleAnalyticsEvent {
  name: AnalyticsEventName;
  dedupeKey: string;
  params: AnalyticsParams;
}

export interface ProductAnalyticsEvent {
  name: AnalyticsEventName;
  params: AnalyticsParams;
}

const STORAGE_PREFIX = 'sbarazzino:analytics:lifecycle:';
const emittedLifecycleKeys = new Set<string>();

function hashKey(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function lifecycleStorageKey(event: LifecycleAnalyticsEvent): string {
  return `${STORAGE_PREFIX}${hashKey(`${event.name}:${event.dedupeKey}`)}`;
}

/**
 * Emits a lifecycle event at most once per browser tab session. The private
 * dedupe key is hashed before storage and is never included in the GA4 payload.
 * A denied event does not consume its key, matching the analytics consent gate.
 */
export function emitLifecycleEventOnce(event: LifecycleAnalyticsEvent): boolean {
  const internalKey = `${event.name}:${event.dedupeKey}`;
  const storage = getSessionStorage();
  const storageKey = lifecycleStorageKey(event);

  if (emittedLifecycleKeys.has(internalKey)) return false;
  try {
    if (storage?.getItem(storageKey) === '1') return false;
  } catch {
    // Storage is best-effort; in-memory deduplication remains available.
  }

  const emitted = trackEvent(event.name, event.params);
  if (!emitted) return false;

  emittedLifecycleKeys.add(internalKey);
  try {
    storage?.setItem(storageKey, '1');
  } catch {
    // Ignore storage failures in hardened/private browser modes.
  }
  return true;
}

export function emitProductEvent(event: ProductAnalyticsEvent): boolean {
  return trackEvent(event.name, event.params);
}

export function secondsBetween(startedAt: number | undefined, endedAt: number): number | undefined {
  if (!startedAt || !Number.isFinite(startedAt) || !Number.isFinite(endedAt)) return undefined;
  return Math.max(0, Math.floor((endedAt - startedAt) / 1000));
}

export function multiplayerCreateEvent(
  matchId: string,
  players: number,
  targetScore: number,
): LifecycleAnalyticsEvent {
  return {
    name: 'multiplayer_create',
    dedupeKey: `create:${matchId}`,
    params: {
      players,
      target_score: targetScore,
    },
  };
}

export function multiplayerJoinEvent(
  matchId: string,
  joinType: 'new' | 'rejoin',
): LifecycleAnalyticsEvent {
  return {
    name: 'multiplayer_join',
    dedupeKey: `${joinType}:${matchId}`,
    params: {
      join_type: joinType,
    },
  };
}

export function onlineGameStartEvent(input: {
  matchId: string;
  players: number;
  targetScore?: number;
  createdAt?: number;
  observedAt?: number;
}): LifecycleAnalyticsEvent {
  const observedAt = input.observedAt ?? Date.now();
  return {
    name: 'game_start',
    dedupeKey: `online:${input.matchId}`,
    params: {
      mode: 'online',
      players: input.players,
      target_score: input.targetScore,
      lobby_duration_seconds: secondsBetween(input.createdAt, observedAt),
    },
  };
}

export function roundCompleteEvent(input: {
  mode: 'cpu' | 'online';
  roundKey: string;
  players: number;
  roundScore?: number;
  totalScore?: number;
  gameStartedAt?: number;
  completedAt?: number;
}): LifecycleAnalyticsEvent {
  const completedAt = input.completedAt ?? Date.now();
  return {
    name: 'round_complete',
    dedupeKey: `${input.mode}:${input.roundKey}`,
    params: {
      mode: input.mode,
      players: input.players,
      round_score: input.roundScore,
      total_score: input.totalScore,
      game_elapsed_seconds: secondsBetween(input.gameStartedAt, completedAt),
    },
  };
}

export function inviteShareEvent(
  content: 'code' | 'link',
  channel: 'clipboard' | 'native' = 'clipboard',
): ProductAnalyticsEvent {
  return {
    name: 'invite_share',
    params: {
      channel,
      surface: 'waiting_room',
      content,
    },
  };
}

export function gameResultShareEvent(
  channel: 'clipboard' | 'native',
  result: 'win' | 'loss' | 'draw',
): ProductAnalyticsEvent {
  return {
    name: 'game_result_share',
    params: {
      channel,
      surface: 'game_over',
      content: 'result',
      mode: 'cpu',
      result,
    },
  };
}

export function createGameResultShareUrl(origin: string): string {
  const url = new URL('/play/', origin);
  url.searchParams.set('mode', 'quick');
  url.searchParams.set('utm_source', 'game_result');
  url.searchParams.set('utm_medium', 'share');
  url.searchParams.set('utm_campaign', 'cpu_result');
  return url.toString();
}

/**
 * `invite` is intentionally distinct from the legacy `join` parameter.
 * `join` is consumed by App as an already-joined room, while a shared invite
 * must first prefill the multiplayer form so Firebase can confirm the join.
 */
export function createMultiplayerInviteUrl(origin: string, matchCode: string): string {
  const url = new URL('/play/', origin);
  url.searchParams.set('invite', matchCode);
  return url.toString();
}

/** Test-only reset for module-scoped deduplication. */
export function resetProductAnalyticsStateForTests(): void {
  emittedLifecycleKeys.clear();
}
